/**
 * Pure estimation functions — extracted from useEstimation hook.
 * Shared by useEstimation (reactive) and useComparison (dual-compute).
 */

import type { EstimationResult, ZIResult, MultiZoneResult, HorizonSnapshot } from '../types/estimation';
import { computeAnnualAmort } from './financeShared';

const ZI_RATIOS = {
  sbp_per_ha: 7700,
  emplois_per_ha: 130,
  infra_per_ha: 800000,
  ep_per_ha: 200,
  ep_pct_surface: 0.10,
};

export const FALLBACK_RATIOS: Record<string, number> = {
  logements_per_ha: 65,
  inhabitants_per_logement: 2.3,
  emplois_per_logement_mixed: 0.80,
  m2_activites_par_emploi: 50,
  sbp_per_logement: 100,
  pct_activites_sbp: 0.12,
  pct_espaces_verts_surface: 0.22,
  classes_per_logements_short: 0.015,
  classes_per_logements_long: 0.01,
  co_per_logements: 0.0004,
  creche_places_per_logements: 0.025,
  sport_threshold: 1200,
  infra_per_logement: 67000,
  ecole_per_class: 2060000,
  creche_per_place: 61000,
  co_cost: 60000000,
  sport_cost: 18000000,
  park_per_m2: 400,
};

export const FALLBACK_BENCHMARKS: Record<string, number> = {
  ecole_sbp_m2_per_class: 604.17,
};

export function estimateZI(ha_zi: number): ZIResult | null {
  if (!ha_zi || ha_zi <= 0) return null;
  const sbp_activites = Math.round(ha_zi * ZI_RATIOS.sbp_per_ha);
  const emplois = Math.round(ha_zi * ZI_RATIOS.emplois_per_ha);
  const espaces_publics = Math.round(ha_zi * 10000 * ZI_RATIOS.ep_pct_surface);
  const cost_infra = Math.round(ha_zi * ZI_RATIOS.infra_per_ha);
  const cost_ep = Math.round(espaces_publics * ZI_RATIOS.ep_per_ha);
  const cost_total = cost_infra + cost_ep;
  return {
    sbp_activites, emplois, espaces_publics,
    cost_infra, cost_ep, cost_total,
    cost_low: Math.round(cost_total * 0.75),
    cost_high: Math.round(cost_total * 1.35),
  };
}

export function computeN1(
  ha: number,
  haZi: number,
  tenantRatios: Record<string, number>,
  tenantBenchmarks: Record<string, number>,
): EstimationResult {
  const r = (key: string): number =>
    tenantRatios[key] ?? FALLBACK_RATIOS[key] ?? 0;
  const b = (key: string): number =>
    tenantBenchmarks[key] ?? FALLBACK_BENCHMARKS[key] ?? 0;

  // ha = total project area; derive residential/mixte portion
  const haMixte = Math.max(0, ha - haZi);

  const logements = Math.round(haMixte * r('logements_per_ha'));
  const inhabitants = Math.round(logements * r('inhabitants_per_logement'));
  const sbp_logements = Math.round(logements * r('sbp_per_logement'));
  const sbp_act_mix = Math.round(sbp_logements * 0.12 / 0.88);
  // v11: emplois = ratio/log + 1 emploi per m² d'activités (default 50 m²/emploi)
  const m2ParEmploi = r('m2_activites_par_emploi') || 50;
  const emplois_mix = Math.round(logements * r('emplois_per_logement_mixed'))
                    + Math.round(sbp_act_mix / m2ParEmploi);
  const espaces_publics = Math.round(haMixte * 10000 * r('pct_espaces_verts_surface'));
  const classes = Math.max(0, Math.round(logements * r('classes_per_logements_short')));
  const sbp_eq = Math.round(classes * b('ecole_sbp_m2_per_class'));
  const sbp_total_mix = sbp_logements + sbp_act_mix + sbp_eq;
  const creche_places = Math.round(logements * r('creche_places_per_logements'));
  const creches = Math.ceil(creche_places / 60);
  const co = Math.floor(logements * r('co_per_logements'));
  const sport = logements >= r('sport_threshold') ? 1 : 0;
  const culturel = Math.floor(logements / 1000);

  const cost_infra_mix = Math.round(logements * r('infra_per_logement'));
  const cost_ecoles = classes > 0 ? Math.round(classes * r('ecole_per_class')) : 0;
  const cost_creches = Math.round(creche_places * r('creche_per_place'));
  const cost_co = co * r('co_cost');
  const cost_sport = sport * r('sport_cost');
  const cost_culturel = culturel * 3000000;
  const cost_ep_mix = Math.round(espaces_publics * r('park_per_m2'));
  const cost_total_mix = cost_infra_mix + cost_ecoles + cost_creches + cost_co + cost_sport + cost_culturel + cost_ep_mix;

  const zi = estimateZI(haZi);
  const emplois = emplois_mix + (zi ? zi.emplois : 0);
  const sbp_act = sbp_act_mix + (zi?.sbp_activites ?? 0);
  const sbp_total = sbp_total_mix + (zi?.sbp_activites ?? 0);
  const cost_total = cost_total_mix + (zi ? zi.cost_total : 0);

  return {
    logements, inhabitants, emplois_mix, emplois,
    sbp_logements, sbp_act_mix, sbp_act, sbp_eq, sbp_total_mix, sbp_total,
    espaces_publics, classes, creche_places, creches, co, sport, culturel,
    cost_infra_mix, cost_ecoles, cost_creches, cost_co, cost_sport, cost_culturel, cost_ep_mix, cost_total_mix,
    zi, cost_total,
    cost_low: Math.round(cost_total * 0.75),
    cost_high: Math.round(cost_total * 1.35),
    density: ha > 0 ? Math.round(logements / ha) : 0,
    ius_implied: ha > 0 ? sbp_total / (ha * 10000) : 0,
  };
}

export function computeN2(
  ha: number,
  haZi: number,
  n2Pct: Record<string, number>,
  tenantRatios: Record<string, number>,
  tenantBenchmarks: Record<string, number>,
  options: { ecoleCT?: boolean } = {},
): EstimationResult {
  const r = (key: string): number =>
    tenantRatios[key] ?? FALLBACK_RATIOS[key] ?? 0;
  const b = (key: string): number =>
    tenantBenchmarks[key] ?? FALLBACK_BENCHMARKS[key] ?? 0;
  const val = (key: string) => {
    const base = r(key);
    const adj = n2Pct[key] ?? 0;
    return base * (1 + adj / 100);
  };

  // ha = total project area; derive residential/mixte portion
  const haMixte = Math.max(0, ha - haZi);

  const logements = Math.round(haMixte * val('logements_per_ha'));
  const inhabitants = Math.round(logements * val('inhabitants_per_logement'));
  const sbp_logements = Math.round(logements * val('sbp_per_logement'));
  const sbp_act_mix = Math.round(sbp_logements * 0.12 / 0.88); // match N1: fixed 12% ratio
  // v11: emplois = ratio/log + 1 emploi per m² d'activités (default 50 m²/emploi)
  const m2ParEmploi = r('m2_activites_par_emploi') || 50;
  const emplois_mix = Math.round(logements * r('emplois_per_logement_mixed'))
                    + Math.round(sbp_act_mix / m2ParEmploi);
  const espaces_publics = Math.round(haMixte * 10000 * val('pct_espaces_verts_surface'));

  // v11: primary classes = CT (short/peak) if ecoleCT toggle is on, else LT (long-term)
  const classes_short = Math.max(0, Math.round(logements * val('classes_per_logements_short')));
  const classes_long = Math.max(0, Math.round(logements * val('classes_per_logements_long')));
  const classes = options.ecoleCT ? classes_short : classes_long;
  const sbp_eq = Math.round(classes * b('ecole_sbp_m2_per_class'));
  const sbp_total_mix = sbp_logements + sbp_act_mix + sbp_eq;
  const creche_places = Math.round(logements * val('creche_places_per_logements'));
  const creches = Math.ceil(creche_places / 60);
  const co = Math.floor(logements * r('co_per_logements'));
  const sport = logements >= r('sport_threshold') ? 1 : 0;
  const culturel = Math.floor(logements / 1000);

  const cost_infra_mix = Math.round(logements * val('infra_per_logement'));
  const cost_ecoles = classes > 0 ? Math.round(classes * val('ecole_per_class')) : 0;
  const cost_creches = Math.round(creche_places * val('creche_per_place'));
  const cost_co = co * val('co_cost');
  const cost_sport = sport * val('sport_cost');
  const cost_culturel = culturel * 3000000;
  const cost_ep_mix = Math.round(espaces_publics * val('park_per_m2'));
  const cost_total_mix = cost_infra_mix + cost_ecoles + cost_creches + cost_co + cost_sport + cost_culturel + cost_ep_mix;

  const zi = estimateZI(haZi);
  const emplois = emplois_mix + (zi ? zi.emplois : 0);
  const sbp_act = sbp_act_mix + (zi?.sbp_activites ?? 0);
  const sbp_total = sbp_total_mix + (zi?.sbp_activites ?? 0);
  const cost_total = cost_total_mix + (zi ? zi.cost_total : 0);

  return {
    logements, inhabitants, emplois_mix, emplois,
    sbp_logements, sbp_act_mix, sbp_act, sbp_eq, sbp_total_mix, sbp_total,
    espaces_publics, classes, classes_short, classes_long,
    ecole_basis: options.ecoleCT ? 'CT' : 'LT',
    creche_places, creches, co, sport, culturel,
    cost_infra_mix, cost_ecoles, cost_creches, cost_co, cost_sport, cost_culturel, cost_ep_mix, cost_total_mix,
    zi, cost_total,
    cost_low: Math.round(cost_total * 0.75),
    cost_high: Math.round(cost_total * 1.35),
    density: ha > 0 ? Math.round(logements / ha) : 0,
    ius_implied: ha > 0 ? sbp_total / (ha * 10000) : 0,
  };
}


/**
 * Compute estimation from total area and ZI area.
 * Residential/mixte hectares are derived as haTotal - haZi.
 * Matches original HTML logic: two inputs → programme mixte derived.
 */
export function computeMultiZone(
  haTotal: number,
  haZi: number,
  _zoneOverrides: Record<string, Record<string, number>>,
  tenantBenchmarks: Record<string, number>,
): MultiZoneResult {
  const combined = computeN1(haTotal, haZi, {}, tenantBenchmarks);
  const industriel = haZi > 0 ? estimateZI(haZi) : null;

  return { quartier: null, mixte: null, industriel, combined };
}


/* ------------------------------------------------------------------
 * Horizon projection helpers
 * ------------------------------------------------------------------ */


/**
 * Generate horizon snapshots at 5-year intervals.
 * Assumes construction in first `constructionYears`, then linear ramp to full occupancy.
 */
export function computeHorizonSnapshots(
  est: EstimationResult,
  horizonYears: number,
  constructionYears = 5,
  revenuePerInhabitant = 2500,
): HorizonSnapshot[] {
  const safeConstruction = Math.max(1, constructionYears);
  const safeHorizon = Math.max(5, horizonYears);
  const snapshots: HorizonSnapshot[] = [];
  const costMap: Record<string, number> = {
    infra: est.cost_infra_mix,
    ep: est.cost_ep_mix,
    ecoles: est.cost_ecoles,
    creches: est.cost_creches,
    co: est.cost_co,
    sport: est.cost_sport,
    culturel: est.cost_culturel,
    zi: est.zi?.cost_total ?? 0,
  };
  const annualAmort = computeAnnualAmort(costMap);
  const totalCost = est.cost_total;

  for (let y = 5; y <= safeHorizon; y += 5) {
    const constructionPct = Math.min(1, y / safeConstruction);
    const rampEnd = safeConstruction + 5;
    const popPct = y >= rampEnd ? 1 : Math.min(1, Math.max(0, (y - 1) / rampEnd));

    const inhabitants = Math.round(est.inhabitants * popPct);
    const logements_delivered = Math.round(est.logements * constructionPct);
    const emplois_delivered = Math.round(est.emplois * constructionPct);
    const cumulative_cost = Math.round(totalCost * constructionPct);
    const annual_revenue = inhabitants * revenuePerInhabitant;
    const annual_amort = y <= safeConstruction ? annualAmort * constructionPct : annualAmort;
    const operationalYears = Math.max(0, y - safeConstruction);
    const cumulative_revenue = Math.round(operationalYears * annual_revenue);
    const annual_net = annual_revenue - annual_amort;
    const cumulative_net = Math.round(
      -cumulative_cost + operationalYears * (annual_revenue - annualAmort)
    );

    snapshots.push({
      year: y,
      inhabitants,
      logements_delivered,
      emplois_delivered,
      cumulative_cost,
      cumulative_revenue,
      annual_amort: Math.round(annual_amort),
      net_balance: Math.round(annual_net),
      cumulative_net,
    });
  }

  return snapshots;
}
